/**
 * Khung ứng dụng: dải cảnh báo NGUY + thanh trên + thanh bên + vùng nội dung +
 * **thanh trạng thái dưới**.
 *
 * Đặc tả: `_BUS/31_QUY_HOACH_APP.md` PHẦN 5B. Bốn thay đổi, xếp theo "mắt nhận ra ngay":
 *
 *   🥇 1 · thanh trạng thái dưới, cố định đáy, KHÔNG BAO GIỜ cuộn mất
 *   🥈 2 · gom 7 mục phẳng thành 5 nhóm có tiêu đề
 *   🥉 3 · dải cảnh báo mức NGUY dính dưới thanh trên — không có thì BIẾN MẤT HẲN
 *      4 · `w-72` → `w-60`, hàng điều hướng thấp lại
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * 🔴 VÌ SAO THANH DƯỚI LÀ THỨ THIẾU QUAN TRỌNG NHẤT (lee)
 *
 * *"Đây là chi tiết mắt dùng để phân biệt 'phần mềm' với 'trang web' nhanh hơn mọi thứ
 * khác. Phần mềm điều khiển LUÔN nói cho người trực biết nó còn sống — không bắt người ta
 * đi tìm."*
 *
 * Nên nó `fixed`, không `sticky`: `sticky` còn phụ thuộc vào chuyện cha nó có cuộn hay
 * không, `fixed` thì không phụ thuộc gì cả.
 *
 * ⚠ Trên điện thoại các mục bấm được vẫn phải cao ≥40px (kỹ thuật viên hiện trường dùng
 *   một tay). Nên hàng điều hướng là **40px ở màn hình nhỏ, 32px từ `lg` trở lên** —
 *   mật độ là việc của màn hình lớn, không phải cái cớ để bóp nút trên điện thoại.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertOctagon, BellRing, ChevronDown, ChevronRight,
  Gauge, KeyRound, LayoutDashboard, LogOut, MapPin, Menu, Moon, Palette, Route, Search, Sparkles,
  ShieldCheck, Sun, Type, X, Zap,
} from 'lucide-react';
/* TK-43: 8 mục menu + logo dùng bộ icon SDKD v01 (mục khác vẫn lucide) */
import {
  IconCaiDatBanDau, IconChienLuoc, IconNangLuong, IconNhatKy, IconQuanLyDen, IconQuanLyTu, IconQuanTri, IconVanHanh, type IconMenu,
} from './icon/iconMenu';
import { LogoSdkd } from './icon/LogoSdkd';
import { CaiUngDung } from './CaiUngDung';
import { useHieuUng } from '../lib/hieuUng';
import { useAuth } from '../lib/auth';
import { OKhoaNho } from './Khoa';
import { useGiaoDien, NHAN_BO_CHU, NHAN_CHE_DO, type BoChu, type CheDo } from '../lib/giaodien';
import { cachHienThanhDay, capDoiTuong, useBeRongCuaSo, useDoiTuongDangChon, type CachHienDay } from '../lib/doiTuongDangChon';
import { useAlarms, useMoPhong, useOverview, useRealtimeStatus, useRealtimeSync, useTree } from '../lib/queries';
import { ROLE_LABEL, type Alarm, type Role, type TrangThaiMoPhong, type TreeCabinet, type TreeRoute } from '../lib/types';
import { Badge, Button, Modal, Field, StatusDot, useToast, LOP_CHUA_RO } from './ui';
import { api, errorMessage } from '../lib/api';
import { fmtTime } from '../lib/format';
import { locMucNguy, phanMuc } from '../lib/canhbao';
import { TrungTamSuCo, useDemSuCo, useDongHeThong } from './HopVanDe';
import { soSuCoMo } from '../lib/vande';
import { lechGioMs, useGioMayChu } from '../lib/giomaychu';
import { hangKhop, soSanhNhay } from '../lib/xephang';
import PopupCanhBao from './PopupCanhBao';
import { useNemaBat } from '../lib/nema';
import { useQueries } from '@tanstack/react-query';
import { khoaTim } from '../lib/khongdau';
import { tenDen } from '../lib/nemaNhan';
/*
 * ➕ `GD-07A` — SỐ PHIÊN BẢN ĐỌC TỪ `package.json`, không gõ tay. Xem `TranhThanhPho()` ở
 *   cuối tệp: một con số gõ tay ở đây sẽ nói dối ngay lần phát hành sau, và nó nói dối đúng
 *   vào lúc người trực gọi điện báo lỗi và được hỏi *"anh đang chạy bản nào"*.
 * ⚠ Nhập trường `version` THÔI, không nhập cả tệp: Vite nhúng thẳng lúc dựng gói, và cách này
 *   không kéo theo danh sách phụ thuộc của dự án vào gói giao diện.
 */
import { version as PHIEN_BAN } from '../../package.json';

interface NavItem {
  to: string;
  /**
   * TÊN ĐẦY ĐỦ — dùng cho `title`, ô gõ-để-nhảy, và mọi chỗ có đủ bề ngang.
   * ⛔ Đây vẫn là tên CHÍNH THỨC của mục (YC-33 chốt "Quản lý Tuyến chiếu sáng").
   */
  label: string;
  /**
   * NHÃN NGẮN cho thanh rail 88px.
   *
   * 🔴 Vì sao phải có trường thứ hai thay vì cắt chuỗi: rail rộng 88px chở được ~10 ký tự.
   *   `label` dài nhất là 24 ký tự ("Quản lý Tuyến chiếu sáng"). Cắt tự động ra
   *   `Quản lý Tuyế…` — một cái tên **không ai đặt**, và người đọc không biết nó là gì.
   *
   * ⛔ Và đây KHÔNG phải tên thứ hai cho cùng một thứ (đúng cái L67 vừa dẹp): nhãn rail là
   *   một **chữ viết tắt có chủ**, còn tên đầy đủ vẫn ở `title` khi rê chuột, ở ô gõ-để-nhảy,
   *   và ở tiêu đề H1 của chính trang ấy. Một tên chính thức, một chữ tắt — không phải hai tên.
   */
  nhanRail: string;
  /** TK-43: kiểu chung (SDKD hoặc lucide) — xem `icon/iconMenu.tsx` */
  icon: IconMenu;
  /*
   * ⛔ `minRole` GIỮ LẠI NHƯNG THÔI DÙNG cho mục nào có `khoa` — `P-6` `B4` mục 3 chuyển ẩn/hiện
   *   sang TICK. Không xoá hẳn trường: ngày có một khu vực chỉ dành cho `SUPER_ADMIN` thì hạng
   *   vẫn là câu hỏi đúng, và máy chủ cũng còn dùng hạng cho đúng ba tuyến ấy.
   */
  minRole?: Role;
  /** Tick phải có để THẤY mục này. Một mục có `khoa` thì `minRole` không còn được đọc. */
  khoa?: string;
  /** Mục cần một CỜ TUYẾN bất kỳ (vd điều khiển) — hạng và tick đều không trả lời được câu này. */
  canCoTuyen?: 'canControl' | 'canEditSchedule';
  end?: boolean;
  /** TK-33b: trang CHƯA CÓ — mục hiện, khoá, không dẫn đi đâu (khoá-không-ẩn). */
  chuaCo?: boolean;
  /**
   * TK-40 (lee Q3): hạng thấp hơn thì mục KHÔNG HIỆN (ngoại lệ có chủ của khoá-không-ẩn — lee: "vai trò khác không thấy menu").
   * Lọc ở `useNhomNav` — MỘT chỗ, mọi nơi đọc menu đều qua đó.
   */
  anDuoiVai?: Role;
}

interface NavGroup {
  /** Tiêu đề nhóm: chữ nhỏ, hoa, nhạt, KHÔNG bấm được. */
  tieuDe: string;
  muc: NavItem[];
}

/**
 * NĂM NHÓM — theo **câu hỏi người trực đang hỏi**, không theo bảng CSDL trang đó đọc
 * *(luật phân mô-đun, PHẦN 1)*.
 *
 * ⚠️ Đây là gom nhóm ở **thanh điều hướng**. Không tệp nào bị đổi tên, không đường dẫn nào
 *    bị đổi, không màn hình nào được thêm — đúng cái lee cho phép chạy trước 0–G.
 *
 * ⚠️ Đặc tả xếp *"Danh sách chờ"* vào nhóm QUẢN TRỊ. Nó **không có đường dẫn riêng** —
 *    nó là một khối bên trong `/con-duong`. Thêm một mục điều hướng cho nó là **thêm màn
 *    hình**, đúng thứ đang bị chặn tới sau 0–G. Nên vẫn là 7 mục, gom thành 5 nhóm.
 */
/** Khoá nhớ trạng thái cột cây. Cùng tiền tố `sdkd.` với các khoá phiên. */
const KHOA_CAY = 'sdkd.cotCay';

const NHOM_NAV: NavGroup[] = [
  /*
   * TK-33b (lee 22/10 chiều, mẫu chạy được `_BUS/thiet_ke/TK-33/SDKD_Smart_City_Lighting.html`) — MỘT danh sách phẳng 8 mục
   *   (mục "Đèn NEMA" theo cờ ⇒ cờ tắt 7). Ánh xạ mẫu → trang Kapp theo bảng phiếu mục 1. "Vấn đề & thông báo" thôi là mục
   *   menu (nó là cột Trung tâm sự cố); /van-de và /dieu-khien vẫn vào được qua ô Ctrl+K (`TRANG_PHU`) và cây tuyến.
   * ⛔ Một nhóm vẫn mang `tieuDe` làm khoá (không vẽ).
   */
  /*
   * TK-41 §2 (lee 24/10): HAI nhóm — VẬN HÀNH (trên) · QUẢN LÝ (dưới cùng thanh bên, có vạch ngăn). Mục không có quyền thì ẨN,
   *   nhóm không còn mục nào thì ẩn cả tiêu đề (`useNhomNav`) ⇒ tài khoản vận hành không thấy nhóm Quản lý.
   */
  {
    tieuDe: 'Vận hành',
    muc: [
      /* TK-38 việc 1 (lee 23/10): đổi tên menu — đường dẫn giữ nguyên để link cũ không gãy */
      { to: '/', label: 'Vận hành', nhanRail: 'Vận hành', icon: IconVanHanh, end: true },
      /* TK-39 §1: Lịch đóng cắt + Chiến lược dim gộp thành MỘT mục (hai tab) */
      { to: '/cai-dat-chien-luoc', label: 'Cài đặt chiến lược', nhanRail: 'Chiến lược', icon: IconChienLuoc },
      /* TK-33b việc 3: trang điện năng có rồi (`/api/dien-nang/ngay`) ⇒ mục mở. Cờ `chuaCo` vẫn giữ cho mục tương lai. */
      { to: '/dien-nang', label: 'Thống kê năng lượng', nhanRail: 'Năng lượng', icon: IconNangLuong },
      /*
       * ⚠ `v4` `S6` — mục này KHÔNG có `khoa`: ai cũng thấy; máy chủ ép `userId = tôi` cho người không có tick
       *   (`requireQuyenHoacCuaToi`). Gác PHẠM VI, không gác LỐI VÀO — một `khoa` thêm vào đây đóng đúng cái vừa mở.
       */
      { to: '/nhat-ky', label: 'Nhật ký thao tác', nhanRail: 'Nhật ký', icon: IconNhatKy },
    ],
  },
  {
    tieuDe: 'Quản lý',
    muc: [
      { to: '/con-duong', label: 'Quản lý tủ', nhanRail: 'Tủ', icon: IconQuanLyTu, khoa: 'quan_ly_tu' },
      /* "Quản trị": tuyến, tick, thời hạn — không chỉ người dùng (`B4` mục 1). */
      { to: '/quan-tri', label: 'Quản trị', nhanRail: 'Quản trị', icon: IconQuanTri, khoa: 'tao_tai_khoan' },
    ],
  },
];

/**
 * Trang KHÔNG có mục menu nhưng vẫn là đích nhảy của ô Ctrl+K (cùng luật `useTrangThaiMuc`).
 */
const TRANG_PHU: NavItem[] = [
  { to: '/van-de', label: 'Vấn đề & thông báo', nhanRail: 'Vấn đề', icon: BellRing },
  { to: '/dieu-khien', label: 'Điều khiển theo tuyến', nhanRail: 'Điều khiển', icon: Gauge, canCoTuyen: 'canControl' },
  /* TK-44 §4: "Bản đồ tuyến" — chỉ xem (xếp đèn là việc của Cài đặt ban đầu) */
  { to: '/ban-do', label: 'Bản đồ tuyến', nhanRail: 'Bản đồ', icon: MapPin },
];

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-31/TK-32 (lee 22/10) — MỤC CỦA ĐÈN NEMA, CHỈ khi máy chủ bật NEMA (`useNemaBat() === 'bat'`).
 *   Cờ tắt / chưa hỏi xong ⇒ menu không có mục NEMA nào.
 * ⛔ MỘT hàm dựng danh sách (`nhomNav`), ba chỗ đọc qua `useNhomNav()` — rail · ngăn kéo · ô gõ-để-nhảy. Thêm mục
 *   vào một chỗ mà quên hai chỗ kia là đúng cái bệnh "ô nhảy tới trang menu không có".
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
const MUC_NEMA: { sau: string; muc: NavItem }[] = [
  /* TK-33b: chèn NGAY SAU mục `sau` (mẫu: Bản đồ · Tủ điều khiển · Đèn NEMA · Lịch …). */
  /* TK-35 lượt 2: "Đèn NEMA" = danh sách TỦ có đèn (đèn thuộc tủ) — trang /nhom-den (nhóm theo tuyến) đã bỏ. */
  /* TK-41: Quản lý đèn thuộc nhóm QUẢN LÝ ⇒ cùng tick `quan_ly_tu` với Quản lý tủ (mẫu: tài khoản vận hành không thấy nhóm Quản lý) */
  { sau: '/con-duong', muc: { to: '/den-nema', label: 'Quản lý đèn', nhanRail: 'Đèn', icon: IconQuanLyDen, khoa: 'quan_ly_tu' } },
  /* TK-40: tab Cài đặt ban đầu (bước 1 khởi tạo NEMA) — chỉ SUPER_ADMIN + ADMIN thấy. `sau` chỉ khớp mục GỐC của NHOM_NAV (mục NEMA chèn rồi không làm mốc được) ⇒ cùng mốc /con-duong, đứng sau Quản lý đèn theo thứ tự mảng */
  { sau: '/con-duong', muc: { to: '/cai-dat-ban-dau', label: 'Cài đặt ban đầu', nhanRail: 'Cài đặt', icon: IconCaiDatBanDau, anDuoiVai: 'ADMIN' } },
];

export function nhomNav(coNema: boolean): NavGroup[] {
  if (!coNema) return NHOM_NAV;
  return NHOM_NAV.map((n) => ({ ...n, muc: n.muc.flatMap((m) => [m, ...MUC_NEMA.filter((x) => x.sau === m.to).map((x) => x.muc)]) }));
}

/**
 * TK-41 §2: mục không có quyền ⇒ ẨN (`ttMuc` = 'khoa'; 'chua-biet' vẫn hiện dấu đang kiểm), hạng thấp hơn `anDuoiVai` ⇒ ẩn;
 * nhóm không còn mục nào ⇒ bỏ cả nhóm (không vẽ tiêu đề trơ trọi). MỘT chỗ — thanh bên, ngăn kéo, ô Ctrl+K cùng đọc.
 */
export function locNhomNav<T extends NavItem>(nhom: { tieuDe: string; muc: T[] }[], thay: (m: T) => boolean) {
  return nhom.map((n) => ({ ...n, muc: n.muc.filter(thay) })).filter((n) => n.muc.length > 0);
}
function useNhomNav(): NavGroup[] {
  const coNema = useNemaBat().trangThai === 'bat';
  const { hasRole } = useAuth();
  const ttMuc = useTrangThaiMuc();
  return useMemo(() => locNhomNav(nhomNav(coNema), (m) => (!m.anDuoiVai || hasRole(m.anDuoiVai)) && ttMuc(m) !== 'khoa'), [coNema, hasRole, ttMuc]);
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * MỘT CHỖ QUYẾT ĐỊNH "THẤY MỤC NÀY KHÔNG" — `P-6` `B4` mục 3.
 *
 * ⛔ BA chỗ đọc `NHOM_NAV` (thanh rail · ngăn kéo · ô gõ-để-nhảy) và trước bản này cả ba đều
 *   tự viết `!m.minRole || hasRole(m.minRole)`. Ba bản của một luật là ba bản sẽ trôi khỏi
 *   nhau — và chỗ trôi sẽ là chỗ **ô gõ-để-nhảy vẫn nhảy tới một trang đã bị giấu khỏi menu**.
 *   Nay một hàm, ba chỗ gọi.
 *
 * ⚠ BA CÂU HỎI KHÁC NHAU, theo đúng thứ tự: tick → cờ tuyến (bất kỳ tuyến nào) → hạng.
 *   Mục không khai gì thì ai cũng thấy (Tổng quan, Lịch, Chiến lược — chúng tự gác bên trong).
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-09` mục 4 — MỘT MỤC MENU CÓ **BA** TRẠNG THÁI, không phải hai.
 *
 * 🔴 HAI LỖI lee chụp 14/09, và chúng là MỘT:
 *     · `Khach_hang_1` có tài khoản phụ (⇒ phải có tick `tao_tai_khoan`) mà **không thấy** mục
 *       Quản trị;
 *     · `khach_hang_11` ở Tổng quan không thấy "Điều khiển theo tuyến", sang trang tủ thì thấy.
 *   Gốc: hàm cũ trả `boolean`, và chỗ gọi **lọc mục đi** khi nó trả `false`. Nên một câu chưa
 *   đo được (`/api/auth/me` chưa trả lời — xem `daBietQuyen`) biến thành một mục **biến mất**.
 *   Menu khác nhau giữa hai trang chỉ vì hai trang được vẽ ở hai thời điểm.
 *
 * ⛔ VÀ CẢ KHI ĐÃ ĐO XONG, ẨN VẪN LÀ SAI Ở ĐÂY. Luật của dự án là **khoá-không-ẩn**: một mục
 *   khoá là một câu trả lời (*"có thứ này, bạn chưa được cấp, đi hỏi ai"*); một khoảng trống
 *   thì không nói gì, và người dùng không biết để mà đi xin. Luật ấy đã áp cho ô tick, cho nút
 *   bấm, cho thẻ tuyến — menu là chỗ cuối cùng còn ẩn.
 * ⚠ NGOẠI LỆ CÓ CHỦ Ý, giữ nguyên: mục "Nhật ký thao tác" KHÔNG khai `khoa` — ai cũng thấy,
 *   nội dung tự co theo quyền. Đó là một quyết định cũ, không phải một chỗ sót.
 *
 * ⇒ Ba trạng thái, và chỗ gọi vẽ cả ba:
 *     `'mo'`        bấm được
 *     `'khoa'`      hiện + ổ khoá hổ phách, bấm vào thì trang nói rõ thiếu tick nào
 *     `'chua-biet'` hiện + xám nhạt, KHÔNG hổ phách (chưa đo được thì chưa khai)
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
type TrangThaiMuc = 'mo' | 'khoa' | 'chua-biet';

/**
 * Dấu bên phải một mục menu — **MỘT chỗ vẽ, hai thanh dùng** (rail + ngăn kéo).
 *
 * ⛔ Hai chỗ tự vẽ là hai chỗ sẽ lệch, và lệch ở đây nghĩa là cùng một tài khoản thấy hai câu
 *   khác nhau tuỳ bề rộng cửa sổ — đúng cái bệnh `GD-09` mục 4 vừa chữa, tái phát ở một chiều
 *   khác.
 * ⚠ `'khoa'` mượn `HuyHieuKhoa` của `Khoa.tsx` (ổ khoá hổ phách, một nguồn cho cả app), nhưng
 *   KHÔNG kèm chữ: ở menu không có chỗ cho một câu, và câu đầy đủ nằm ở trang đích.
 */
function DauTrangThaiMuc({ tt }: { tt: TrangThaiMuc }) {
  if (tt === 'mo') return null;
  if (tt === 'chua-biet') {
    /* Xám, đập nhẹ — "đang đo", không phải "không có quyền" (`[95]`). */
    return (
      <span
        className="ml-auto h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-chua-ro"
        title="Đang kiểm quyền…"
        aria-label="Đang kiểm quyền"
      />
    );
  }
  /*
   * ⛔ Ổ KHOÁ LẤY TỪ `Khoa.tsx` — biểu tượng ấy chỉ được vẽ ở MỘT chỗ trong cả app. Bản đầu
   *   tôi nhập `Lock` của `lucide-react` vào đây và bộ kiểm đỏ đúng: hai chỗ tự vẽ là hai cái
   *   ổ khoá sẽ khác nhau, mà người dùng nhận ra "khoá" bằng HÌNH chứ không bằng tên lớp.
   */
  return <OKhoaNho className="ml-auto h-3.5 w-3.5 shrink-0" />;
}

function useTrangThaiMuc(): (m: NavItem) => TrangThaiMuc {
  const { hasRole, coQuyen, user, daBietQuyen } = useAuth();
  return (m: NavItem) => {
    /*
     * ⛔ HỎI "ĐÃ BIẾT CHƯA" TRƯỚC MỌI CÂU KHÁC — và chỉ cho những mục THẬT SỰ hỏi quyền.
     *   Một mục không khai gì (Tổng quan, Lịch, Chiến lược) thì không có gì để chờ biết.
     */
    if ((m.khoa || m.canCoTuyen) && !daBietQuyen) return 'chua-biet';
    if (m.khoa) return coQuyen(m.khoa) ? 'mo' : 'khoa';
    if (m.canCoTuyen) {
      /* “Có ÍT NHẤT một tuyến mang cờ ấy” — `SUPER_ADMIN` qua bằng vế bỏ qua. */
      if (user?.role === 'SUPER_ADMIN') return 'mo';
      return (user?.routeAccess ?? []).some((r) => r[m.canCoTuyen!]) ? 'mo' : 'khoa';
    }
    if (m.minRole) return hasRole(m.minRole) ? 'mo' : 'khoa';
    return 'mo';
  };
}


/*
 * Cao 28px (`h-7`). Thanh bên và vùng nội dung đều phải chừa đúng chừng này.
 *
 * 🔴 CHIỀU CAO thanh trạng thái, và CHỖ ĐỨNG ngay trên nó — MỘT nguồn, BA người dùng.
 *   Thanh Gửi của trang Cài đặt phải dính đáy NGAY TRÊN thanh này. Viết `bottom-7` ở đó và
 *   `h-7` ở đây là hai con số phải bằng nhau mà không gì bắt chúng bằng nhau — đổi một cái
 *   là cái kia âm thầm sai, và chỉ ẢNH mới thấy (đã gặp: `bottom-0` bị thanh này đè lên nút).
 */
/* TK-33b: thanh dưới 28 px MỘT dòng ở mọi bề rộng (mẫu `footer{height:28px}`); điện thoại bỏ bớt chữ phụ, không gói dòng. */
export const CAO_THANH_DUOI = 'h-[calc(1.75rem+env(safe-area-inset-bottom))] pb-[env(safe-area-inset-bottom)]';
export const DAY_THANH_DUOI = 'bottom-[calc(1.75rem+env(safe-area-inset-bottom))]';

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-32 B.1/B.5 (22/10) — KHÔNG CÒN DẢI NÀO dưới thanh trên. Dải vàng "N việc mức 1" bỏ (chuông là lối
 *   vào duy nhất); dải "ĐANG MÔ PHỎNG" thu thành MỘT NHÃN trên thanh trên (`NhanMoPhong`). Nên độ lệch
 *   của `aside`/`main` là HẰNG — không còn bảng "theo số dải".
 * ⚠ Vẫn là chuỗi Tailwind viết sẵn (không sinh chuỗi lúc chạy): Tailwind chỉ biên dịch lớp nó THẤY.
 *   Ai thêm lại một dải `fixed` dưới thanh trên thì phải dựng lại bảng độ lệch — đừng chồng dải lên nội dung.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
/* TK-33b: thanh trên 62 px (mẫu `header{height:62px}`), phủ hết bề ngang; thanh bên đứng NGAY DƯỚI nó. */
export const TOP_BEN = 'top-[calc(62px+env(safe-area-inset-top))]';
export const CHUA_TREN = 'pt-4';

export default function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  /*
   * Cột cây mở hay đóng — nhớ giữa các lần mở trang.
   * ⚠ Bọc `try/catch`: cửa sổ riêng tư / trình duyệt chặn lưu trữ thì `localStorage` NÉM,
   *   và một thanh điều hướng không được phép làm sập cả trang vì một tuỳ chọn hiển thị.
   */
  /*
   * ➕ `GD-02` — MẶC ĐỊNH **ĐÓNG**, đảo lại so với bản cũ.
   *
   * 🔴 Mẫu lee duyệt chỉ có MỘT thanh bên 200 px; cột cây tuyến không có trong đó. Nhưng cây
   *   ấy là một đường đi thật *(từ bất kỳ trang nào tới thẳng một cái tủ)*, nên **xoá nó là
   *   lấy mất một chức năng để đổi lấy một cái ảnh**. ⇒ Giữ nguyên cột, chỉ đổi mặc định:
   *   màn hình mở ra đúng như mẫu, và ai cần cây thì bấm một nút — nút vẫn ở chỗ cũ.
   * ⚠ `=== '1'` chứ không `!== '0'`: khoá VẮNG MẶT nay nghĩa là đóng. Người đã từng mở cây
   *   thì `'1'` còn trong `localStorage` và họ vẫn thấy nó mở — không ai bị giật mất thứ họ
   *   đã chọn.
   */
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ⚠ `GD-04` 11/09/2026 — MẶC ĐỊNH ĐẢO LẠI LẦN NỮA: **MỞ ở màn ≥ 1280 px, ĐÓNG ở màn hẹp**.
   *   Và tôi ghi cả ba lần đổi lại đây, vì một mặc định bị lật ba lần trong hai ngày là thứ
   *   lần sau người ta sẽ lật lần thứ tư mà không biết vì sao nó từng là thế:
   *       bản đầu   MỞ      — cột cây là đường đi nhanh tới một cái tủ
   *       `GD-02`   ĐÓNG    — mẫu Tổng quan chỉ có MỘT thanh bên 200 px
   *       `GD-04`   theo BỀ RỘNG — mẫu `24` vẽ cây ấy **nằm trong ảnh**, cạnh menu
   *   Mẫu `GD-02` không vẽ cây vì nó là mẫu của một màn hẹp hơn; mẫu `GD-04` vẽ nó. Hai mẫu
   *   không mâu thuẫn — chúng nói về hai bề rộng khác nhau, và đó chính là câu trả lời.
   *
   * 🔴 BA CA, VÀ CA GIỮA LÀ CA DỄ NUỐT: khoá `'1'`/`'0'` là **lựa chọn người dùng đã nói ra**
   *   và nó thắng mọi mặc định; khoá **VẮNG MẶT** nghĩa là *"người này chưa bao giờ nói gì"*,
   *   và chỉ lúc ấy mới hỏi tới bề rộng màn hình. Đọc `null` thành `'0'` là biến "chưa chọn"
   *   thành "đã chọn đóng" — Luật số 0, ở một chỗ trông như chuyện vặt.
   * ⚠ `matchMedia` bọc `try/catch` cùng với `localStorage`: cả hai đều ném được (cửa sổ riêng
   *   tư, môi trường dựng sẵn không có `window`), và một tuỳ chọn hiển thị không được phép
   *   làm sập cả trang.
   * ⚠ Đo MỘT LẦN lúc dựng, không theo dõi liên tục: người dùng kéo hẹp cửa sổ rồi cây tự
   *   đóng lại giữa lúc họ đang đọc là một cú giật, không phải một sự giúp đỡ. Lớp `lg:` của
   *   Tailwind đã lo phần ẩn cột ở màn hẹp.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  const [moCay, setMoCayRaw] = useState<boolean>(() => {
    try {
      const daChon = localStorage.getItem(KHOA_CAY);
      if (daChon === '1') return true;
      if (daChon === '0') return false;
      return window.matchMedia('(min-width: 1280px)').matches;
    } catch { return false; }
  });
  const datMoCay = (v: boolean) => {
    setMoCayRaw(v);
    try { localStorage.setItem(KHOA_CAY, v ? '1' : '0'); } catch { /* không sao */ }
  };
  const location = useLocation();
  const { data: moPhong } = useMoPhong();
  /* TK-32 B.1: MỘT ngăn kéo "Vấn đề" cho cả app — chuông (thanh trên) và "N cảnh báo" (thanh dưới) cùng mở nó. */
  const [hopMo, setHopMo] = useState(false);
  const topBen = TOP_BEN;
  const chuaTren = CHUA_TREN;

  const [oNhayMo, setONhayMo] = useState(false);

  /*
   * ⌨️ Ctrl+K / ⌘K. Nghe ở `window` chứ không ở một phần tử — người dùng có thể đang đứng
   *   bất kỳ đâu trong app khi họ muốn nhảy.
   * ⚠ `preventDefault()` là bắt buộc: Chrome gán Ctrl+K cho thanh địa chỉ của chính nó.
   */
  useEffect(() => {
    const nghe = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setONhayMo((v) => !v);
      }
    };
    window.addEventListener('keydown', nghe);
    return () => window.removeEventListener('keydown', nghe);
  }, []);

  // Nghe WebSocket một lần cho cả app
  useRealtimeSync();

  // Đổi trang thì đóng ngăn kéo trên điện thoại
  useEffect(() => { setDrawerOpen(false); setHopMo(false); }, [location.pathname]);

  /*
   * TK-33b: trang chính `/` là BẢN ĐỒ VẬN HÀNH tràn khung (mẫu: ô số · bản đồ · bảng tủ · cột sự cố sát mép) ⇒ khung không
   *   đệm lề cho nó; mọi trang khác giữ lề như cũ.
   */
  /* TK-44 §5: chế độ xếp vị trí của Cài đặt ban đầu (`?xep=1`) chiếm trọn chiều cao màn — cũng tràn khung, không đệm lề */
  const cheDoXep = location.pathname === '/cai-dat-ban-dau' && new URLSearchParams(location.search).get('xep') === '1';
  const tranKhung = location.pathname === '/' || cheDoXep;
  /*
   * TK-33b: chuông mở cột Trung tâm sự cố ở trang KHÔNG có sẵn cột (ngăn kéo). Trang chính ở màn ≥ 1024 px ĐÃ có cột ⇒ đưa
   *   tiêu điểm vào nó thay vì mở bản thứ hai chồng lên.
   */
  const moHop = () => {
    const cot = document.getElementById('trung-tam-su-co');
    if (tranKhung && cot && cot.offsetParent !== null) { cot.focus(); cot.scrollIntoView({ block: 'nearest' }); return; }
    setHopMo(true);
  };

  return (
    <div className="min-h-screen bg-nen-0">
      {/*
        ═══════════════════════════════════════════════════════════════════════════════
        TK-33b (mẫu chạy được `SDKD_Smart_City_Lighting.html`) — THANH TRÊN 62 px phủ hết bề ngang (logo mặt trời + "SDKD"
          | "Trung tâm điều hành chiếu sáng"); THANH BÊN 186 px đứng DƯỚI nó tới thanh dưới 28 px. Thanh bên chở 8 mục +
          nhóm "KHU VỰC VẬN HÀNH" = cây Tuyến › Tủ (thay cột cây riêng của bản 1 — cây vẫn gập/mở và NHỚ lựa chọn,
          `KHOA_CAY`). Điện thoại (< 768): thanh bên thành ngăn kéo (nút ≡).
        ═══════════════════════════════════════════════════════════════════════════════
      */}
      <TopBar onMenu={() => setDrawerOpen(true)} moODeNhay={() => setONhayMo(true)} moHop={moHop} moPhong={moPhong} />

      <aside className={`fixed ${topBen} ${DAY_THANH_DUOI} left-0 z-30 hidden w-[186px] border-r border-vien bg-nen-ben md:block`} data-o="thanh-ben">
        <ThanhBenChinh moCay={moCay} datMoCay={datMoCay} moPhong={moPhong} />
      </aside>

      <div className="md:ml-[186px]">
        {/* `pb-` để dòng cuối không nằm dưới thanh dưới 28 px */}
        <main className={tranKhung ? 'min-w-0 pb-7' : `min-w-0 px-3 pb-12 ${chuaTren} sm:px-5`}>
          <Outlet />
        </main>
      </div>

      {/* Ngăn kéo trên điện thoại — chở ĐÚNG thanh bên (một danh sách mục + cây, một chỗ dựng). */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-nen-0/80" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-[86%] max-w-xs flex-col overflow-y-auto bg-nen-ben pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-xl">
            <div className="flex justify-end px-2 pt-2">
              <button onClick={() => setDrawerOpen(false)} className="rounded-nut p-1 text-chu-3 hover:bg-nen-2" aria-label="Đóng menu">
                <X className="h-5 w-5" />
              </button>
            </div>
            <ThanhBenChinh moCay={moCay} datMoCay={datMoCay} moPhong={moPhong} />
          </aside>
        </div>
      )}

      <ThanhTrangThai moPhong={moPhong} />
      <NganKeoVanDe mo={hopMo} dong={() => setHopMo(false)} />
      <ODeNhay mo={oNhayMo} dong={() => setONhayMo(false)} />
      {/*
        🔴 POPUP MỨC 1 (YC-29 PHẦN 2) — treo ở KHUNG, không ở một trang.
        Người trực đang mở trang Lịch mà một tủ mất bảo vệ thì họ vẫn phải thấy. Đặt trong
        một trang là làm cảnh báo phụ thuộc vào chỗ người ta đang đứng.
      */}
      <PopupCanhBao />
    </div>
  );
}

/* --------------------------------------------- 🔔 ngăn kéo "Vấn đề" (TK-32 B.1) */

/**
 * Bấm chuông (hoặc "N cảnh báo" ở thanh dưới) ⇒ ngăn kéo bên PHẢI chở `HopVanDe pham="he-thong"` —
 * cùng một khung với Tổng quan và `/van-de`, không phải danh sách thứ hai. Đóng: nút ✕, bấm nền, Esc,
 * hoặc đổi trang (Layout đặt lại khi `pathname` đổi).
 * ⚠ Chỉ DỰNG khi mở: không mở thì không có truy vấn "Đã xử lý 7 ngày" chạy ngầm trên mọi trang.
 */
function NganKeoVanDe({ mo, dong }: { mo: boolean; dong: () => void }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!mo) return;
    const nghe = (e: KeyboardEvent) => { if (e.key === 'Escape') dong(); };
    window.addEventListener('keydown', nghe);
    return () => window.removeEventListener('keydown', nghe);
  }, [mo, dong]);
  if (!mo) return null;
  return (
    <div className="fixed inset-0 z-50" data-o="ngan-keo-van-de">
      <div className="absolute inset-0 bg-nen-0/70" onClick={dong} />
      <aside role="dialog" aria-label="Trung tâm sự cố"
        className="absolute inset-y-0 right-0 flex w-full max-w-[360px] flex-col overflow-hidden border-l border-vien bg-nen-0 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-xl">
        <div className="flex items-center gap-2 px-4 pt-3">
          <Link to="/van-de" className="text-xs font-semibold text-nhan hover:underline">Mở trang Vấn đề</Link>
          <button onClick={dong} className="ml-auto rounded-nut p-1.5 text-chu-3 hover:bg-nen-2 hover:text-chu-1" aria-label="Đóng">
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* TK-33b: cùng thành phần với cột phải trang chính — "Định vị" đóng ngăn kéo rồi chọn tủ trên trang chính. */}
        <TrungTamSuCo className="min-h-0 flex-1 pt-2" onDinhVi={(id) => { dong(); navigate(`/?tu=${id}`); }} />
      </aside>
    </div>
  );
}

/* --------------------------------------------- 🧪 nhãn "MÔ PHỎNG NGÕ VÀO" (TK-32 B.5) */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * NHÃN THƯỜNG TRỰC KHI MÁY CHỦ ĐANG MÔ PHỎNG NGÕ VÀO — điều kiện 3 của `services/mophong.ts`.
 *   TK-32 B.5: trước là một DẢI cao `h-8` dưới thanh trên; nay thu thành MỘT NHÃN trên thanh trên,
 *   câu đầy đủ ở `title`. LUẬT GIỮ NGUYÊN.
 *
 * dev_device: *"Luồng mô phỏng hai ngõ vào mà không có dải báo thì đúng ngày nào đó có người
 * đọc số mô phỏng như số thật. Cùng họ `manualHold` luôn `false`."*
 *
 * ⛔ BỐN ĐIỀU CỐ Ý, MỖI ĐIỀU CHẶN MỘT ĐƯỜNG HỎNG CỤ THỂ:
 *
 * 1 · **KHÔNG có nút đóng, không có `useState`.** Nhãn nói về một ĐIỀU KIỆN của cả máy chủ: nó chỉ
 *     được tắt bằng cách tắt mô phỏng. Cho đóng được là cho người trực tự tay xoá thứ duy nhất bảo
 *     họ đừng tin số trên màn hình — và ca sau ngồi vào là không còn dấu nào.
 *
 * 2 · **Chữ `dau` lấy từ máy chủ, không gõ lại `[MÔ PHỎNG]` ở đây.** Một dấu hai nơi khai là
 *     một dấu sẽ lệch (xem `types.ts`).
 *
 * 3 · **Tên ngõ lấy từ `tt.ngoVao`, không viết tay.** Ngày dev_device thêm ngõ thứ ba, nhãn này
 *     tự kể đúng ba ngõ. Viết tay ⇒ nhãn khai hai ngõ trong khi ba ngõ đang sinh số giả.
 *
 * 4 · **Hiện ở MỌI bề rộng** (không `hidden sm:`) — trên điện thoại chỉ còn chữ ngắn, câu đủ ở `title`.
 *
 * ⚠ CHỖ NHÃN NÀY KHÔNG CỨU ĐƯỢC, nói thẳng ra: mất máy chủ ⇒ `tt` là `undefined` ⇒ nhãn biến mất,
 *   trong khi các cảnh báo mô phỏng cũ vẫn còn trong bộ đệm trình duyệt. Đó chính là lý do dấu
 *   `[MÔ PHỎNG]` nằm TRONG câu cảnh báo chứ không phải chỉ ở nhãn này — hai lớp, và lớp trong
 *   cùng đi theo câu chữ tới tận bản in. Đừng bao giờ rút lớp ấy vì "đã có nhãn rồi".
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
function NhanMoPhong({ tt }: { tt: TrangThaiMoPhong | undefined }) {
  if (tt?.bat !== true) return null;
  /* Danh sách rỗng phải NÓI RA: `bat = true` mà không khai ngõ nào là máy chủ bật một cửa sinh cảnh báo
     mà giao diện không biết nó sinh cho cái gì. */
  const ten = tt.ngoVao.length > 0
    ? tt.ngoVao.map((n) => n.ten).join(' · ')
    : '(máy chủ không khai ngõ nào)';
  return (
    <span data-o="nhan-mo-phong"
      title={`${tt.dau} ĐANG MÔ PHỎNG NGÕ VÀO: ${ten} — cảnh báo từ các ngõ này do MÁY CHỦ sinh ra để chạy thử luồng, không phải phép đo từ tủ.`}
      className="inline-flex shrink-0 items-center whitespace-nowrap rounded-[3px] border border-canh/40 px-[7px] py-1 text-[11px] text-canh">
      {/* TK-33b: chữ của mẫu ("Dữ liệu minh họa", viền vàng); câu đầy đủ + tên ngõ vẫn ở `title`. */}
      Dữ liệu minh họa
    </span>
  );
}

/* ------------------------------------------ 🥇 thanh trạng thái dưới */

/**
 * `● Máy chủ: NỐI │ Tủ 12/14 online │ ⚠ 2 cảnh báo │ Giờ máy chủ 15:42:07`
 *
 * ⚠ Mọi con số ở đây dùng `.so-do` *(tabular-nums)* — `12/14` phải **đứng yên** khi đổi,
 *   nếu không cả thanh nhấp nháy mỗi 30 giây và mắt thôi tin nó.
 *
 * ⚠ Nhãn giờ đổi theo `theoMayChu`: đo được lệch giờ ⇒ *"Giờ máy chủ"*; chưa đo được ⇒
 *   *"Giờ máy"* + xám nghiêng. **Không vẽ giờ trình duyệt dưới nhãn máy chủ.**
 */
/* ------------------------------------------- 🥇 ô "gõ để nhảy" (PHẦN 6.4a) */

interface MucNhay {
  /** `trang` · `duong` · `tu` — quyết định biểu tượng. */
  loai: 'trang' | 'duong' | 'tu';
  nhan: string;
  /** Dòng thứ hai, nhạt — mã, địa chỉ, trạng thái. Có thể rỗng. */
  phu?: string;
  to: string;
  /**
   * MÃ ĐỊNH DANH — thứ người ta gõ khi họ muốn **đúng cái này**: mã tủ, `hardwareId`, mã đường.
   * Khớp TIỀN TỐ ở đây thắng mọi khớp khác.
   */
  ma: string;
  /** Tên đọc được. Khớp tiền tố ở đây đứng hạng hai. */
  ten: string;
  /** Mọi thứ còn lại đem đi so: địa chỉ, nhãn cảnh báo đang mở, mã thiết bị. */
  them: string;
  /** Có cảnh báo mức 1 đang mở không — dùng để phá hoà, không dùng để lọc. */
  dangNguy: boolean;
}

/**
 * Ô GÕ ĐỂ NHẢY — `Ctrl+K`. Đặc tả `31_QUY_HOACH_APP.md` PHẦN 6.4a, việc 2 của PHẦN 7.3.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * 🎯 Vì sao nó nằm ở **thanh dưới** chứ không phải góc trên phải
 *
 * Qt Creator để `Type to locate (Ctrl+K)` ở góc **trái thanh dưới**. Ô tìm ở góc trên phải là
 * gu mạng xã hội — và đặc tả 6.0 đã liệt nó vào danh sách **BỎ**.
 *
 * > *"Ô gõ để nhảy là thứ biến một trang web thành một công cụ."*
 *
 * ⚠️ Thanh bên **vẫn phải đầy đủ**: nó là đường học của người mới. Ô này là **lối tắt**, không
 *   phải lối thay thế — nên nó không được phép giấu bớt thứ gì mà thanh bên đang có.
 *
 * ⛔ Và nó **KHÔNG thêm một màn hình nào**: không có `path=` mới, không có `<Route>` mới. Nó là
 *   một lớp phủ nhảy tới những trang đã có. Đó là lý do việc này chạy được trước 0–G.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
function ODeNhay({ mo, dong }: { mo: boolean; dong: () => void }) {
  const nhomMenu = useNhomNav();
  const ttMuc = useTrangThaiMuc();
  const { data: routes } = useTree();
  const { data: canhBao } = useAlarms();
  const navigate = useNavigate();
  const [tu, setTu] = useState('');
  const [chon, setChon] = useState(0);
  const oNhap = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mo) { setTu(''); setChon(0); setTimeout(() => oNhap.current?.focus(), 0); }
  }, [mo]);

  /*
   * Nguồn: đúng những thứ thanh bên đã có, không hơn. Ô tắt mà chỉ ra được thứ thanh bên
   * không có thì hai chỗ đã trôi khỏi nhau — và người dùng học ở chỗ này rồi không tìm nổi
   * ở chỗ kia.
   */
  const tatCa = useMemo<MucNhay[]>(() => {
    // Nhãn cảnh báo ĐANG MỞ của từng tủ — để gõ "mất công tơ" ra được "cái nào đang hỏng".
    const nhanTheoTu = new Map<string, { chu: string[]; nguy: boolean }>();
    for (const a of canhBao ?? []) {
      const g = phanMuc(a.code, a.mucDo);
      const o = nhanTheoTu.get(a.cabinetId) ?? { chu: [], nguy: false };
      o.chu.push(g.nhan);
      if (g.mucDo === 1) o.nguy = true;
      nhanTheoTu.set(a.cabinetId, o);
    }

    const ra: MucNhay[] = [];
    for (const nhom of nhomMenu) {
      for (const n of nhom.muc) {
        /*
         * ⚠ Ô GÕ-ĐỂ-NHẢY CHỈ NHẬN MỤC `'mo'`. Đây KHÔNG phải "ẩn": ô này là một cái phím tắt,
         *   và một phím tắt nhảy vào trang báo "không đủ quyền" thì vô dụng hơn là không có.
         *   Mục khoá vẫn HIỆN ở thanh bên kèm ổ khoá — đó mới là chỗ trả lời câu "vì sao".
         * ⛔ `'chua-biet'` cũng không nhận: chưa đo được thì chưa mời người ta nhảy vào.
         */
        if (ttMuc(n) !== 'mo' || n.chuaCo) continue;
        ra.push({
          loai: 'trang', nhan: n.label, phu: nhom.tieuDe, to: n.to,
          ma: '', ten: n.label.toLowerCase(), them: nhom.tieuDe.toLowerCase(), dangNguy: false,
        });
      }
    }
    /* TK-33b: trang không có mục menu (Vấn đề · Điều khiển theo tuyến · Bản đồ toàn màn hình) — cùng luật quyền. */
    for (const n of TRANG_PHU) {
      if (ttMuc(n) !== 'mo') continue;
      ra.push({ loai: 'trang', nhan: n.label, phu: 'Trang', to: n.to, ma: '', ten: n.label.toLowerCase(), them: '', dangNguy: false });
    }
    for (const r of routes ?? []) {
      ra.push({
        loai: 'duong', nhan: r.tenDayDu, phu: `${r.onlineCount}/${r.cabinetCount} tủ trực tuyến`,
        to: '/dieu-khien',
        ma: (r.code ?? '').toLowerCase(),
        ten: r.tenDayDu.toLowerCase(),
        them: '',
        dangNguy: false,
      });
      for (const c of r.cabinets) {
        const cb = nhanTheoTu.get(c.id);
        ra.push({
          loai: 'tu', nhan: c.name,
          phu: cb ? `${c.code} · ${cb.chu[0]}` : `${c.code} · ${r.tenDayDu}`,
          to: `/tu/${c.id}`,
          // ⚠ `hardwareId` đứng trong MÃ ĐỊNH DANH: kỹ thuật viên ngoài hiện trường đọc mã
          //   trên vỏ tủ, và đó thường là thứ DUY NHẤT họ có trong tay lúc đứng dưới cột đèn.
          ma: `${c.code} ${c.hardwareId}`.toLowerCase(),
          ten: c.name.toLowerCase(),
          them: `${r.tenDayDu} ${c.deviceId ?? ''} ${(cb?.chu ?? []).join(' ')}`.toLowerCase(),
          dangNguy: cb?.nguy ?? false,
        });
      }
    }
    return ra;
  }, [routes, ttMuc, canhBao, nhomMenu]);

  const kq = useMemo(() => {
    const k = tu.trim().toLowerCase();
    if (!k) return tatCa.filter((m) => m.loai === 'trang');
    /*
     * ⚠ `thuTu` = vị trí trong cây, và nó CHỈ dùng để phá hoà ở nấc thứ ba. Bản đầu dùng nó
     *   làm thứ tự duy nhất — gõ một từ chung chung thì kết quả xếp vô nghĩa.
     */
    return tatCa
      .map((m, thuTu) => ({ m, thuTu, hang: hangKhop(m, k), dangNguy: m.dangNguy }))
      .filter((x) => x.hang !== 9)
      .sort(soSanhNhay)
      .slice(0, 40)
      .map((x) => x.m);
  }, [tu, tatCa]);

  useEffect(() => { setChon(0); }, [tu]);

  if (!mo) return null;

  const nhay = (m?: MucNhay) => { if (m) { navigate(m.to); dong(); } };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]" role="dialog" aria-modal="true" aria-label="Gõ để nhảy">
      <div className="absolute inset-0 bg-nen-0/80" onClick={dong} />
      <div className="relative w-[92%] max-w-xl overflow-hidden rounded-the border border-vien-manh bg-nen-1 shadow-xl">
        <div className="flex items-center gap-2 border-b border-vien px-3">
          <Search className="h-4 w-4 shrink-0 text-chu-3" />
          <input
            ref={oNhap}
            value={tu}
            onChange={(e) => setTu(e.target.value)}
            placeholder="Gõ tên tủ, mã tủ, mã phần cứng, tên đường, hoặc tên trang…"
            className="w-full bg-transparent py-3 text-sm text-chu-1 outline-none placeholder:text-chu-3"
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.preventDefault(); dong(); }
              else if (e.key === 'ArrowDown') { e.preventDefault(); setChon((v) => Math.min(v + 1, kq.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setChon((v) => Math.max(v - 1, 0)); }
              else if (e.key === 'Enter') { e.preventDefault(); nhay(kq[chon]); }
            }}
          />
          <kbd className="hidden shrink-0 rounded border border-vien px-1.5 py-0.5 text-xs text-chu-3 sm:block">Esc</kbd>
        </div>

        <div className="thin-scroll max-h-[52vh] overflow-y-auto py-1">
          {kq.length === 0 && (
            // ⚠ Nói ra là KHÔNG TÌM THẤY. Một danh sách rỗng không chú thích thì người dùng
            //   tưởng ô hỏng — cùng luật với "chưa có số liệu" của `Metric`.
            <p className="px-3 py-6 text-center text-sm text-chu-3">
              Không có tủ, đường hay trang nào khớp “{tu}”.
            </p>
          )}
          {kq.map((m, i) => (
            <button
              key={`${m.loai}-${m.to}-${i}`}
              onMouseEnter={() => setChon(i)}
              onClick={() => nhay(m)}
              className={[
                'flex w-full items-center gap-2.5 px-3 py-2 text-left',
                i === chon ? 'bg-nhan/15' : 'hover:bg-nen-2',
              ].join(' ')}
            >
              {m.loai === 'trang' ? <LayoutDashboard className="h-4 w-4 shrink-0 text-chu-3" />
                : m.loai === 'duong' ? <MapPin className="h-4 w-4 shrink-0 text-chu-3" />
                : <Zap className="h-4 w-4 shrink-0 text-chu-3" />}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-chu-1">{m.nhan}</span>
                {m.phu && <span className="block truncate text-xs text-chu-3">{m.phu}</span>}
              </span>
              {/* Mức NGUY hiện thành MỘT CHẤM, không phải một màu chữ khác — ba mức là ba
                  HÌNH DẠNG (PHẦN 2 luật 3), và ở đây chỉ cần phân biệt "có" với "không". */}
              {m.dangNguy && <AlertOctagon className="h-3.5 w-3.5 shrink-0 text-nguy" />}
              <span className="shrink-0 text-xs uppercase tracking-wide text-chu-3">
                {m.loai === 'trang' ? 'trang' : m.loai === 'duong' ? 'đường' : 'tủ'}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 border-t border-vien px-3 py-1.5 text-xs text-chu-3">
          <span>↑↓ chọn</span><span>↵ nhảy tới</span><span>Esc đóng</span>
          <span className="ml-auto so-do">{kq.length} kết quả</span>
        </div>
      </div>
    </div>
  );
}

/**
 * TK-45 §2 + TK-46 §3 (lee 25/10, ảnh z01 / z03): ô "đang thao tác trên…" giữa thanh đáy — tuyến › tủ › đèn của trang đang mở.
 *   ⛔ Cả chip `min-w-0 overflow-hidden` (bản TK-45 thiếu ⇒ cấp cuối `shrink-0` đẩy chữ ra NGOÀI khung, đè chữ bên cạnh).
 *   Thiếu chỗ: tuyến CO TRƯỚC (`flex-shrink: 100`, trần px theo `cachHienThanhDay`) → chỉ còn biểu tượng; cấp CUỐI giữ. Tên đủ ở `title`.
 */
function ODoiTuong({ cach }: { cach: CachHienDay }) {
  const dt = useDoiTuongDangChon();
  const cap = capDoiTuong(dt);
  if (cap.length === 0) return null;
  const chu = cap.join(' › ');
  const tuyen = dt?.tuyen?.trim() ? dt.tuyen : null;
  const sau = capDoiTuong({ tu: dt?.tu, den: dt?.den });
  return (
    <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} title={chu} data-o="doi-tuong-dang-chon"
      className="flex min-w-0 max-w-full items-center gap-1 overflow-hidden rounded border border-nhan/40 bg-nhan/10 px-2 py-px text-[11px] font-semibold text-nhan">
      {tuyen && (cach.tuyen === 'bieu-tuong' && sau.length > 0
        ? <Route className="h-3 w-3 shrink-0" aria-label={tuyen} data-o="tuyen-bieu-tuong" />
        : <span className="min-w-0 truncate [flex-shrink:100]" style={cach.tranTuyen && sau.length > 0 ? { maxWidth: cach.tranTuyen } : undefined} data-o="tuyen-chu">{tuyen}</span>)}
      {sau.map((c, i) => (
        <span key={i} className="flex min-w-0 items-center gap-1">
          {(tuyen || i > 0) && <span className="shrink-0 opacity-60">›</span>}
          <span className="min-w-0 truncate" data-o="cap-sau">{c}</span>
        </span>
      ))}
    </button>
  );
}

function ThanhTrangThai({ moPhong }: { moPhong: TrangThaiMoPhong | undefined }) {
  /*
   * TK-33b (mẫu `footer`, 28 px, chữ 10 px): "Máy chủ kết nối" (chấm xanh) / "Mất kết nối máy chủ" (XÁM) · "x/y tủ trực tuyến" ·
   *   "Dữ liệu thời gian thực" | "Chế độ mô phỏng" · phải: giờ HH:MM:SS + ngày — GIỜ MÁY CHỦ (`useGioMayChu`, chưa đo lệch ⇒
   *   "(giờ máy)" xám). Mẫu có "MQTT ổn định" — Kapp không dùng MQTT ⇒ BỎ.
   * ⚠ Mọi số dùng `.so-do` (tabular-nums) — số phải ĐỨNG YÊN khi đổi.
   */
  const status = useRealtimeStatus();
  const ov = useOverview();
  const gio = useGioMayChu();
  const o = ov.data;
  const on = status === 'open';
  /* TK-46 §3: thiếu chỗ thì nhường theo thứ tự cố định (một hàm thuần) */
  const cach = cachHienThanhDay(useBeRongCuaSo());
  const ngay = (() => { const d = new Date(Date.now() + (lechGioMs() ?? 0)); const h = (n: number) => String(n).padStart(2, '0'); return `${h(d.getDate())}/${h(d.getMonth() + 1)}/${d.getFullYear()}`; })();
  return (
    <footer
      className={`fixed bottom-0 left-0 right-0 z-30 flex ${CAO_THANH_DUOI} items-center gap-x-[22px] overflow-hidden whitespace-nowrap border-t border-vien bg-nen-0 px-[15px] text-[10px] text-chu-2`}
      role="status" aria-live="off" data-o="thanh-duoi"
    >
      <span className="flex shrink-0 items-center gap-1.5" title="Kênh realtime tới máy chủ">
        <StatusDot online={on} size="sm" pulse={status === 'connecting'} />
        {cach.chuMayChu && <span className={on ? 'text-chu-2' : 'text-chu-3'} data-o="chu-may-chu">{on ? 'Máy chủ kết nối' : status === 'connecting' ? 'Đang nối máy chủ' : 'Mất kết nối máy chủ'}</span>}
      </span>
      {cach.soTu && <span className="shrink-0" data-o="so-tu-truc-tuyen"><span className="so-do text-chu-1">{o ? `${o.online}/${o.cabinets}` : '—/—'}</span> tủ trực tuyến</span>}
      {cach.cheDo && <span className="shrink-0" data-o="che-do-du-lieu">{moPhong?.bat === true ? 'Chế độ mô phỏng' : 'Dữ liệu thời gian thực'}</span>}
      <span className="flex min-w-0 flex-1 justify-center overflow-hidden"><ODoiTuong cach={cach} /></span>
      <time className="ml-auto shrink-0" title={gio.theoMayChu ? 'Giờ máy chủ' : 'CHƯA đọc được giờ máy chủ — đây là giờ của máy này'}>
        {gio.theoMayChu ? <span className="so-do">{gio.gio}</span> : <span className={LOP_CHUA_RO}><span className="so-do">{gio.gio}</span> (giờ máy)</span>}
        <span className="hidden sm:inline"> {ngay}</span>
      </time>
    </footer>
  );
}

/* ------------------------------------------------------------ thanh trên */

function TopBar({ onMenu, moODeNhay, moHop, moPhong }: {
  onMenu: () => void; moODeNhay: () => void; moHop: () => void; moPhong: TrangThaiMoPhong | undefined;
}) {
  const { user, signOut } = useAuth();
  const status = useRealtimeStatus();
  /* TK-33b: số trên chuông = sự cố ĐANG MỞ — `soSuCoMo` (số máy chủ đếm, không trần 100), cùng hàm với ô số + Trung tâm sự cố. */
  const { dong } = useDongHeThong();
  const dem = useDemSuCo();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const navigate = useNavigate();

  const statusText = status === 'open'
    ? 'Realtime đang chạy'
    : status === 'connecting' ? 'Đang kết nối realtime…' : 'Mất kết nối realtime';
  const soChuong = soSuCoMo(dem, dong);

  return (
    <header className="sticky top-0 z-30 flex h-[calc(62px+env(safe-area-inset-top))] items-center gap-3 border-b border-vien bg-nen-0 px-3 pt-[env(safe-area-inset-top)] md:gap-[22px] md:px-[22px]" data-o="thanh-tren">
      <button onClick={onMenu} className="rounded-nut p-2 text-chu-2 hover:bg-nen-2 md:hidden" aria-label="Mở menu">
        <Menu className="h-5 w-5" />
      </button>

      {/* Logo: dấu SDKD (TK-43, bộ icon v01 — trước: mặt trời lucide) + "SDKD" | "Trung tâm điều hành chiếu sáng" (vế sau ẩn dưới 1200 px như mẫu). */}
      <Link to="/" className="flex shrink-0 items-center gap-3 whitespace-nowrap text-[15px] font-bold text-chu-1 md:text-[19px]" data-o="logo">
        <LogoSdkd className="h-5 w-5 shrink-0 text-nhan" />
        SDKD
        <span className="hidden border-l border-vien pl-4 text-[13px] font-medium xl:inline">Trung tâm điều hành chiếu sáng</span>
      </Link>

      {/*
        * ➕ `GD-02` — Ô Ctrl+K RỘNG theo mẫu (TK-33b: tối đa 560 px).
        * ⛔ LOGIC GIỮ NGUYÊN: nó vẫn chỉ MỞ `ODeNhay` — cùng một ô gõ-để-nhảy, cùng nguồn dữ
        *   liệu, cùng phím tắt. Đây là một cái nút trông như ô nhập, không phải một ô nhập thứ
        *   hai: hai ô tìm là hai chỗ sẽ trôi khỏi nhau.
        */}
      <button
        onClick={moODeNhay}
        className="flex h-[38px] min-w-0 max-w-[560px] flex-1 items-center gap-2 rounded-nut border border-vien bg-nen-lom px-3 text-left text-[13px] text-chu-2 transition hover:border-vien-manh"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="truncate">Tìm tuyến, tủ, đèn… (Ctrl+K)</span>
      </button>

      <div className="ml-auto flex items-center gap-3 md:gap-[22px]">
        {/* TK-48 §0.6: Cài ứng dụng — màn rộng ở đây, màn hẹp trong menu tài khoản; đã cài ⇒ không hiện */}
        <span className="hidden md:inline-flex"><CaiUngDung /></span>
        <span className="hidden items-center whitespace-nowrap text-xs lg:inline-flex" title={statusText} data-o="trang-thai-may-chu">
          <span className={`mr-1.5 inline-block h-[7px] w-[7px] rounded-full ${status === 'open' ? 'bg-tot' : 'bg-chu-3'}`} />
          <span className={status === 'open' ? 'text-tot' : 'text-chu-3'}>{status === 'open' ? 'Trực tuyến' : status === 'connecting' ? 'Đang nối…' : 'Mất kết nối máy chủ'}</span>
        </span>
        <NhanMoPhong tt={moPhong} />

        {/*
          * CHUÔNG — lối vào cột Trung tâm sự cố ở trang không có sẵn cột (ngăn kéo).
          * ⛔ Con số = sự cố ĐANG MỞ, KHÔNG hiện khi bằng 0: một chuông luôn có số bên cạnh là một chuông mắt thôi nhìn.
          */}
        <button
          type="button"
          onClick={moHop}
          data-o="chuong"
          aria-label={soChuong > 0 ? `${soChuong} sự cố đang mở — mở Trung tâm sự cố` : 'Mở Trung tâm sự cố'}
          title={soChuong > 0 ? `${soChuong} sự cố đang mở` : 'Không có sự cố đang mở'}
          className="relative flex h-9 w-9 items-center justify-center rounded-nut border border-vien text-chu-2 transition hover:bg-nen-2 hover:text-chu-1"
        >
          <BellRing className="h-[17px] w-[17px]" strokeWidth={1.8} />
          {soChuong > 0 && (
            <span className="absolute -right-1.5 -top-1.5 min-w-[18px] rounded-nut bg-nguy px-1 text-center text-[11px] font-semibold leading-[18px] text-nen-0" data-o="so-chuong">
              {soChuong}
            </span>
          )}
        </button>

        <div className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-[9px] rounded-nut border-0 px-1 py-1 hover:bg-nen-2"
            title={user ? `@${user.username}` : undefined}
          >
            {/* Mẫu: chữ cái đầu trong vòng 30 px + tên. */}
            <span className="grid h-[30px] w-[30px] place-items-center rounded-full bg-nen-3 text-[13px] text-chu-1">
              {(user?.fullName || user?.username || '?').slice(0, 1).toUpperCase()}
            </span>
            <span className="hidden whitespace-nowrap text-[13px] text-chu-1 sm:inline">{user?.fullName || user?.username}</span>
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-xl border border-vien bg-nen-1 shadow-lg">
                <div className="border-b border-vien px-3 py-2">
                  <p className="text-sm font-medium text-chu-1">{user?.fullName}</p>
                  <p className="text-xs text-chu-2">@{user?.username}</p>
                  <Badge tone="blue" className="mt-1">{user ? ROLE_LABEL[user.role] : ''}</Badge>
                </div>
                {/*
                  * ═══════════════════════════════════════════════════════════════════════
                  * ➕ `GD-02` — MỤC "GIAO DIỆN": hai câu hỏi ĐỘC LẬP, hai hàng công tắc.
                  *
                  * ⛔ KHÔNG đóng menu sau khi bấm. Đây là chỗ người ta **thử rồi nhìn**:
                  *   bấm Sáng, liếc màn hình, bấm lại Tối. Đóng menu mỗi lần bấm là bắt họ
                  *   mở lại ba lần cho một quyết định.
                  * ⚠ Và hai hàng KHÔNG gộp thành một danh sách bốn lựa chọn: chế độ và bộ
                  *   chữ là hai trục, gộp lại là bắt đổi cả hai khi chỉ muốn đổi một.
                  * ═══════════════════════════════════════════════════════════════════════
                  */}
                <div className="border-b border-vien px-3 py-2">
                  <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-chu-3">
                    <Palette className="h-3.5 w-3.5" /> Giao diện
                  </p>
                  <ChonGiaoDien />
                  <CongTacHieuUng />
                </div>
                <span className="md:hidden"><CaiUngDung dang="menu" /></span>
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-chu-1 hover:bg-nen-2"
                  onClick={() => { setMenuOpen(false); setPwdOpen(true); }}
                >
                  <KeyRound className="h-4 w-4 text-chu-3" /> Đổi mật khẩu
                </button>
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-nguy hover:bg-nguy-nen"
                  onClick={async () => { setMenuOpen(false); await signOut(); navigate('/dang-nhap'); }}
                >
                  <LogOut className="h-4 w-4" /> Đăng xuất
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </header>
  );
}

/**
 * TK-48 §0.5: công tắc HIỆU ỨNG (mặc định tắt). Hệ thống ưu tiên giảm chuyển động ⇒ khoá, nói lý do bằng `title`.
 */
function CongTacHieuUng() {
  const fx = useHieuUng();
  return (
    <button type="button" aria-pressed={fx.chay} disabled={fx.giamChuyenDong} onClick={fx.doi} data-o="cong-tac-hieu-ung"
      title={fx.giamChuyenDong ? 'Thiết bị đang bật giảm chuyển động' : undefined}
      className={`mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-nut px-2 py-1.5 text-xs transition disabled:opacity-50 ${fx.chay ? 'bg-nhan text-tren-nhan' : 'bg-nen-2 text-chu-2 hover:text-chu-1'}`}>
      <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />Hiệu ứng {fx.chay ? 'bật' : 'tắt'}
    </button>
  );
}

/**
 * Hai hàng công tắc: chế độ và bộ chữ. Nhãn lấy từ `lib/giaodien.tsx` — một chỗ viết.
 *
 * ⚠ `aria-pressed` chứ không `aria-checked`: đây là hai nhóm NÚT BẬT, không phải hộp kiểm.
 *   Trình đọc màn hình đọc ra "đã chọn / chưa chọn" đúng nghĩa người dùng đang gặp.
 */
function ChonGiaoDien() {
  const { cheDo, boChu, datCheDo, datBoChu } = useGiaoDien();
  const o = (dangChon: boolean) =>
    `flex-1 rounded-nut px-2 py-1.5 text-xs transition ${
      dangChon ? 'bg-nhan text-tren-nhan' : 'bg-nen-2 text-chu-2 hover:text-chu-1'}`;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-1.5">
        {(['toi', 'sang'] as CheDo[]).map((v) => (
          <button key={v} type="button" aria-pressed={cheDo === v} className={o(cheDo === v)}
            onClick={() => datCheDo(v)}>
            <span className="flex items-center justify-center gap-1.5">
              {v === 'toi' ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
              {NHAN_CHE_DO[v]}
            </span>
          </button>
        ))}
      </div>
      <div className="flex gap-1.5">
        {(['segoe', 'lexend', 'bevietnam'] as BoChu[]).map((v) => (
          <button key={v} type="button" aria-pressed={boChu === v} className={o(boChu === v)}
            onClick={() => datBoChu(v)}>
            <span className="flex items-center justify-center gap-1.5">
              <Type className="h-3.5 w-3.5" />
              {NHAN_BO_CHU[v]}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------- đổi mật khẩu */

export function ChangePasswordModal({
  open, onClose, forced = false,
}: { open: boolean; onClose: () => void; forced?: boolean }) {
  const { changePassword } = useAuth();
  const toast = useToast();
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (newPwd.length < 6) return setErr('Mật khẩu mới phải từ 6 ký tự trở lên');
    if (newPwd !== confirm) return setErr('Hai lần nhập mật khẩu mới không khớp');
    setBusy(true);
    try {
      await changePassword(oldPwd, newPwd);
      toast.success('Đã đổi mật khẩu');
      setOldPwd(''); setNewPwd(''); setConfirm('');
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={forced ? () => { /* bắt buộc đổi, không cho đóng */ } : onClose}
      hoiKhiDong={!forced} /* TK-27: bắt buộc đổi thì hộp không đóng được — hỏi "bỏ?" là hỏi thừa */
      title="Đổi mật khẩu"
      subtitle={forced ? 'Tài khoản đang dùng mật khẩu tạm — bắt buộc đổi trước khi vào hệ thống.' : undefined}
      footer={
        <>
          {!forced && <Button variant="secondary" onClick={onClose} disabled={busy}>Huỷ</Button>}
          <Button variant="primary" onClick={submit} loading={busy}>Lưu mật khẩu mới</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Mật khẩu hiện tại" required>
          <input className="inp" type="password" value={oldPwd} autoComplete="current-password"
            onChange={(e) => setOldPwd(e.target.value)} />
        </Field>
        <Field label="Mật khẩu mới" required hint="Tối thiểu 6 ký tự">
          <input className="inp" type="password" value={newPwd} autoComplete="new-password"
            onChange={(e) => setNewPwd(e.target.value)} />
        </Field>
        <Field label="Nhập lại mật khẩu mới" required error={err ?? undefined}>
          <input className="inp" type="password" value={confirm} autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
        </Field>
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------- thanh bên */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * THANH RAIL — icon lớn, nhãn nhỏ phía dưới (lee chốt 05/09, theo mẫu anh gửi).
 *
 * ⚠ Rail rộng 88px chở được ~10 ký tự một dòng, nên mỗi mục dùng `nhanRail` (chữ tắt có
 *   chủ) chứ KHÔNG cắt `label`. Tên đầy đủ vẫn tới người dùng qua `title` khi rê chuột và
 *   qua ô gõ-để-nhảy — xem ghi chú ở `NavItem.nhanRail`.
 *
 * ⛔ Tiêu đề nhóm (GIÁM SÁT / ĐIỀU KHIỂN / …) BỎ khỏi rail: 88px không đủ cho chúng, và một
 *   tiêu đề bị cắt còn tệ hơn không có. Thay bằng một đường kẻ mảnh giữa hai nhóm — cùng
 *   công dụng phân cụm, không tốn bề ngang. Nhóm nào rỗng vì phân quyền thì KHÔNG kẻ, để
 *   đường kẻ không trở thành một cụm rỗng.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * THANH BÊN CHÍNH — 200 px, theo mẫu lee duyệt (`GD-02`).
 *
 * Bốn tầng, đúng thứ tự của mẫu:
 *     logo  ·  danh sách mục  ·  thẻ xanh "tủ gần nhất"  ·  Đăng xuất
 *
 * ⛔ THAY CHO RAIL 88 px + NHÃN VIẾT TẮT. Và đó là một thứ **lấy lại được** chứ không chỉ đổi
 *   dáng: rail cũ phải mang `nhanRail` — một cái tên viết tắt thứ hai cho cùng một mục, tồn
 *   tại chỉ vì 88 px không chở nổi *"Quản lý Tuyến chiếu sáng"*. 200 px chở được tên thật.
 * ⚠ `nhanRail` GIỮ NGUYÊN trong `NavItem` (ô gõ-để-nhảy vẫn đọc nó và các nhóm vẫn khai nó);
 *   chỉ thanh bên thôi dùng. Xoá một trường đang có người đọc là một việc khác, cho một phiếu
 *   khác.
 *
 * ⚠ CÁC NHÓM (`NHOM_NAV`) vẫn còn — chúng phân quyền và gom việc — nhưng mẫu vẽ một danh sách
 *   PHẲNG, nên tiêu đề nhóm không hiện. Ranh giới nhóm còn lại một đường kẻ mảnh.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
/**
 * 🔴 `giua` — CHỖ CHÈN GIỮA nav và khối đáy ("Đang mở" / Đăng xuất / phiên bản).
 *
 * ⛔ VÌ SAO PHẢI LÀ MỘT CHỖ CHÈN chứ không phải dựng cây SAU thành phần này: khối đáy mang
 *   `mt-auto`, nên trong ngăn kéo điện thoại `ThanhBenChinh` ăn hết chiều cao và mọi thứ
 *   dựng sau nó bị đẩy khỏi tầm nhìn. Lee báo đúng triệu chứng ấy: *"Không tìm thấy danh
 *   sách các tủ theo tuyến trên giao diện ipad, phone"* — cây CÓ dựng, chỉ là không ai cuộn
 *   tới. Một thứ phải cuộn mới thấy, trên màn hình người ta cầm một tay ngoài hiện trường,
 *   là một thứ không có.
 */
function ThanhBenChinh({ moCay, datMoCay, moPhong }: {
  moCay: boolean; datMoCay: (v: boolean) => void; moPhong: TrangThaiMoPhong | undefined;
}) {
  const ttMuc = useTrangThaiMuc();
  const { data: routes } = useTree();
  /* TK-44 §4 (lee 24/10): ở Cài đặt ban đầu KHÔNG có cây "Khu vực vận hành" — trang có danh sách chọn tủ riêng; cây ở đó dễ bấm
     nhầm sang tủ khác lúc đang cài */
  /* TK-48 (bản v06): trang Vận hành có cây "Tuyến & thiết bị" của nó (cột / ngăn kéo) ⇒ thanh bên không lặp cây */
  const duongCay = useLocation().pathname;
  const anCay = duongCay === '/' || duongCay.startsWith('/cai-dat-ban-dau');

  /*
   * ⛔ `GD-09` mục 4 — KHÔNG LỌC NỮA. Mọi mục đều hiện; trạng thái đi kèm từng mục (mở · khoá · đang kiểm · chưa có).
   */
  const nhomMenu = useNhomNav();
  const nhomThay = nhomMenu.map((n) => ({ ...n, muc: n.muc.map((m) => ({ m, tt: ttMuc(m) })) }));
  /* Mẫu: nút trái, chữ #b5c0ca, đệm 13×12; mục chọn nền `#18313e` (`nen-3`) chữ xanh, vạch trái 3 px. */
  const LOP_MUC = 'flex items-center gap-2 rounded-nut px-3 py-[13px] text-[13px] transition';

  return (
    <nav className="thin-scroll flex h-full min-h-0 flex-col gap-[5px] overflow-y-auto px-2.5 py-3.5" aria-label="Điều hướng chính">
      {nhomThay.map((nhom, iNhom) => (
        <div key={nhom.tieuDe} data-nhom-menu={nhom.tieuDe}
          className={`flex flex-col gap-[5px] ${iNhom > 0 ? 'order-last mt-auto border-t border-vien pt-3' : ''}`}>
          <p className="m-0 px-3 pb-1.5 pt-1 text-[10.5px] uppercase tracking-[0.14em] text-chu-2">{nhom.tieuDe}</p>
          {nhom.muc.map(({ m: n, tt }) => n.chuaCo ? (
            /* Trang CHƯA CÓ: hiện, mờ, không bấm — khoá-không-ẩn, không trỏ tới đường chết. */
            <span key={n.to} aria-disabled="true" title={`${n.label} — chưa có trang`} data-chua-co
              className={`${LOP_MUC} cursor-not-allowed text-chu-3 opacity-60`}>
              <n.icon strokeWidth={1.8} className="h-[20px] w-[20px] shrink-0" />
              <span className="truncate">{n.label}</span>
            </span>
          ) : (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              title={tt === 'khoa' ? `${n.label} — bạn chưa được cấp quyền này` : n.label}
              className={({ isActive }) =>
                [
                  LOP_MUC,
                  /* ⛔ Token, KHÔNG mã màu viết thẳng — đúng thứ phép [39] chặn. */
                  isActive ? 'border-l-[3px] border-nhan bg-nen-3 pl-[9px] text-nhan' : 'text-chu-2 hover:bg-nen-2 hover:text-chu-1',
                  /*
                   * ⚠ MỤC KHOÁ VẪN BẤM ĐƯỢC — và đó là chủ ý: trang đích nói rõ THIẾU TICK NÀO
                   *   và AI cấp được, còn một cái menu thì không đủ chỗ cho câu ấy. Mờ đi là để
                   *   mắt phân biệt, không phải để chặn.
                   */
                  tt === 'mo' ? '' : 'opacity-60',
                ].join(' ')
              }
            >
              {/* TK-43: icon menu 20 px (bộ SDKD khuyên 20–24; trước 17 px với lucide) */}
              <n.icon strokeWidth={1.8} className="h-[20px] w-[20px] shrink-0" />
              {/* TK-38: "Thống kê năng lượng" dài hơn thanh bên 186 px ⇒ xuống dòng, không cắt chữ */}
              <span className="min-w-0 leading-tight">{n.label}</span>
              <DauTrangThaiMuc tt={tt} />
            </NavLink>
          ))}
        </div>
      ))}

      {/* KHU VỰC VẬN HÀNH — cây Tuyến › Tủ của Kapp (không có cấp thành phố/phường). Gập/mở nhớ qua `KHOA_CAY`. */}
      {!anCay && (
        <button type="button" onClick={() => datMoCay(!moCay)} aria-expanded={moCay}
          className="mt-[22px] flex items-center gap-1.5 border-0 border-t border-vien px-2.5 pb-2 pt-5 text-left text-[11px] text-chu-2 hover:text-chu-1"
          data-o="khu-vuc-van-hanh">
          <span className="flex-1">KHU VỰC VẬN HÀNH</span>
          {moCay ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
      )}
      {moCay && !anCay && <CayKhuVuc />}

      {/* Đáy: "Phiên mô phỏng" khi máy chủ mô phỏng; số phiên bản. TK-47 §2: bỏ ô "<tủ> · Xem tủ". */}
      <div className="order-last px-2.5 pb-1 pt-3.5 text-[11px] text-chu-2" data-o="day-thanh-ben">
        {moPhong?.bat === true ? (
          <p className="m-0 flex items-center"><span className="mr-1.5 inline-block h-[7px] w-[7px] rounded-full bg-canh" />Phiên mô phỏng</p>
        ) : null}
        <TranhThanhPho />
      </div>
    </nav>
  );
}

/**
 * Cây "KHU VỰC VẬN HÀNH": Tuyến › Tủ. Mỗi tủ một chấm trạng thái (xanh đang nối · XÁM mất kết nối); bấm tủ ⇒ trang chính
 * chọn tủ ấy (`/?tu=<id>`: bản đồ bay tới + bảng tủ). Tuyến mở sẵn khi chứa tủ đang chọn.
 */
/** Đèn tối thiểu cho ô tìm của cây (cùng khoá truy vấn với lớp đèn trang Vận hành ⇒ dùng chung bộ nhớ đệm, không tải trùng). */
type DenTim = { cabinetId: string; addr: number; name: string | null; stt: number | null; tuyenId?: string | null };
function CayKhuVuc() {
  const { data: routes, isLoading, isError } = useTree();
  const [timKw, setTimKw] = useState('');
  /* TK-48 §4: ô tìm tìm cả ĐÈN — chỉ tải danh sách đèn khi cờ NEMA bật VÀ ô tìm có chữ */
  const coNema = useNemaBat().trangThai === 'bat';
  const ids = coNema && timKw.trim() ? (routes ?? []).map((r) => r.id) : [];
  const qs = useQueries({ queries: ids.map((id) => ({ queryKey: ['nema', 'tuyen', id, 'den'] as const,
    queryFn: () => api.get<{ den: DenTim[] }>(`/api/nema/tuyen/${id}/den`), staleTime: 10_000 })) });
  const denKhop = useMemo(() => {
    const k = khoaTim(timKw); const m = new Map<string, DenTim[]>();
    if (!k) return m;
    qs.forEach((q, i) => (q.data?.den ?? []).forEach((d) => {
      if (!khoaTim(tenDen(d)).includes(k)) return;
      const a = m.get(d.cabinetId) ?? []; a.push({ ...d, tuyenId: ids[i] }); m.set(d.cabinetId, a);
    }));
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timKw, qs.map((q) => q.dataUpdatedAt).join(',')]);
  const loc = useMemo(() => filterTree(routes ?? [], timKw, denKhop), [routes, timKw, denKhop]);
  return (
    <div className="px-1.5 pb-2" data-o="cay-khu-vuc">
      <label className="relative mb-1.5 block">
        <span className="sr-only">Tìm tuyến, tủ, đèn</span>
        <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-chu-3" />
        <input className="inp min-h-0 py-1 pl-7 text-xs" placeholder={coNema ? 'Tìm tuyến, tủ, đèn' : 'Tìm tuyến, tủ'} value={timKw} onChange={(e) => setTimKw(e.target.value)} data-o="tim-cay-ben" />
      </label>
      {isLoading && <p className="px-2 py-2 text-xs text-chu-3">Đang tải…</p>}
      {isError && <p className="px-2 py-2 text-xs text-chu-3">Không tải được cây tuyến</p>}
      {!isLoading && !isError && loc.length === 0 && <p className="px-2 py-2 text-xs text-chu-3">{timKw ? 'Không thấy' : 'Chưa có tuyến nào'}</p>}
      {loc.map((r) => <RouteNode key={r.id} route={r} forceOpen={timKw.trim().length > 0} denKhop={denKhop} />)}
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-07A` 11/09/2026 — TRANH THÀNH PHỐ + KHẨU HIỆU + PHIÊN BẢN, đáy thanh trái (mẫu `27`).
 *
 * 🔴 VÌ SAO MỘT BỨC TRANH LẠI ĐÁNG CHỖ TRONG MỘT CÔNG CỤ VẬN HÀNH: nó không phải trang trí —
 *   nó là chỗ **neo phiên bản**. Người trực gọi điện báo lỗi thì câu đầu tiên phải trả lời là
 *   *"anh đang chạy bản nào"*, và cho tới hôm nay con số ấy không có mặt ở đâu trên màn hình.
 *   Ba đèn đường sáng trong tranh chỉ là cách làm cho cái chỗ ấy có người nhìn tới.
 *
 * ⛔ SỐ PHIÊN BẢN ĐỌC TỪ `package.json`, KHÔNG GÕ TAY. Một con số gõ tay ở đây là con số sẽ
 *   nói dối ngay lần phát hành sau — và nó nói dối đúng vào lúc người ta tin nó nhất.
 * ⚠ `resolveJsonModule` đã bật sẵn trong `tsconfig`; Vite nhúng thẳng lúc dựng gói, không có
 *   lời gọi mạng nào.
 *
 * ⛔ MÀU TRONG SVG ĐI QUA TOKEN, không một mã màu gõ tay: `currentColor` + `text-*`, và hai
 *   `stop-color` của dải dùng `rgb(var(--nhan) / …)`. Bức tranh này nằm trong `Layout.tsx`,
 *   tệp mà phép `[39]` đếm — một `#16305c` ở đây là một chỗ đứng im khi lee gạt sang chế độ
 *   sáng, và nó sẽ là cái vệt tối duy nhất trên một thanh bên trắng.
 * ⚠ `aria-hidden` + `focusable="false"`: trình đọc màn hình không có gì để nói về một bức
 *   tranh trang trí, còn khẩu hiệu và số phiên bản là chữ thật nên chúng vẫn đọc được.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
/*
 * GD-16 mục 8 — BỎ tranh minh hoạ + khẩu hiệu "Thắp sáng những con đường xanh" khỏi thanh bên
 *   (được đưa sang trang đăng nhập). GIỮ số phiên bản: người trực gọi báo lỗi cần biết bản nào,
 *   và đó là chữ thật, không phải trang trí (L-KHÔNG-PHỒNG cấm minh hoạ, không cấm số phiên bản).
 */
function TranhThanhPho() {
  return (
    <p className="mt-3 px-1.5 pb-1 text-xs text-chu-3">v{PHIEN_BAN}</p>
  );
}

function filterTree(routes: TreeRoute[], keyword: string, denKhop: Map<string, unknown[]> = new Map()): TreeRoute[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return routes;
  return routes
    .map((r) => {
      // Ô tìm khớp trên CẢ tên LẪN địa chỉ — gõ "Thủ Đức" phải ra được.
      const routeHit = `${r.tenDayDu} ${r.code ?? ''}`.toLowerCase().includes(kw);
      const cabinets = routeHit
        ? r.cabinets
        : r.cabinets.filter((c) => `${c.name} ${c.code} ${c.hardwareId}`.toLowerCase().includes(kw) || (denKhop.get(c.id)?.length ?? 0) > 0);
      return { ...r, cabinets };
    })
    .filter((r) => r.cabinets.length > 0);
}

/* TK-34 lượt 3 (mẫu V3): cây tuyến hai dòng — tên ngắn + địa chỉ mờ, CẢ HAI tách từ `tenDayDu` ("Tên — Phường, Tỉnh"): không dòng nào
   in `route.name` trơ (luật lee), và thiếu địa chỉ thì dòng một chính là `tenDayDu`. */
function tachTenDayDu(t: string): { ten: string; diaChi: string | null } {
  const i = t.indexOf(' — ');
  return i < 0 ? { ten: t, diaChi: null } : { ten: t.slice(0, i), diaChi: t.slice(i + 3) };
}

function RouteNode({ route, forceOpen, denKhop }: { route: TreeRoute; forceOpen: boolean; denKhop: Map<string, DenTim[]> }) {
  const params = useParams();
  const navigate = useNavigate();
  const loc = useLocation();
  /* TK-33b: tủ đang chọn trên trang chính (`/?tu=`) cũng mở sẵn tuyến của nó. */
  const tuQ = new URLSearchParams(loc.search).get('tu');
  const tuyenQ = loc.pathname === '/' ? new URLSearchParams(loc.search).get('tuyen') : null;
  const containsCurrent = route.cabinets.some((c) => c.id === params.id || c.id === params.cabinetId || c.id === tuQ);
  const [open, setOpen] = useState(containsCurrent);

  useEffect(() => {
    if (containsCurrent || forceOpen) setOpen(true);
  }, [containsCurrent, forceOpen]);

  /*
   * ⛔ LUẬT YC-35 — CHỈ CỘNG NHỮNG TỦ ĐANG NỐI. `isOn` đến từ `CabinetState.outputs[].on`, và
   *   kênh ấy KHÔNG có chu kỳ: tủ mất kết nối thì cột ấy đông lạnh ở lần liên lạc cuối. Đang
   *   nối ⇒ số là hiện tại dù bản tin cũ bao nhiêu; mất nối ⇒ không biết, đừng cộng.
   */
  const tuDemDuoc = route.cabinets.filter((c) => c.status === 'ONLINE');
  const onCount = tuDemDuoc.reduce((n, c) => n + c.outputs.filter((o) => o.isOn).length, 0);
  const soKhongDem = route.cabinets.length - tuDemDuoc.length;

  return (
    <div className="mb-0.5">
      <button
        /* TK-48 §4: bấm tuyến ⇒ trang Vận hành CHỌN tuyến ấy (`/?tuyen=`) và mở nhánh; bấm lại đúng tuyến đang chọn ⇒ gập */
        onClick={() => { if (tuyenQ === route.id && open) { setOpen(false); return; } setOpen(true); navigate(`/?tuyen=${route.id}`); }}
        aria-expanded={open}
        aria-current={tuyenQ === route.id && !tuQ ? 'true' : undefined}
        data-tuyen-cay={route.id}
        title={`${route.onlineCount}/${route.cabinetCount} tủ trực tuyến · ${onCount} ngõ ra bật${soKhongDem > 0 ? ` (tủ tự khai · ${soKhongDem} tủ không nói thay được)` : ' (tủ tự khai)'}`}
        className="flex min-h-[40px] w-full items-center gap-1.5 rounded-nut border-0 px-2 py-1.5 text-left text-xs text-chu-1 hover:bg-nen-2 md:min-h-[30px]"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-chu-3" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-chu-3" />}
        {/* 🔴 tenDayDu, KHÔNG phải route.name — sidebar toàn "Nguyễn Huệ" trơ trọi là
            sidebar không chọn được (chốt YC-21). */}
        <span className="min-w-0 flex-1 leading-tight" title={route.tenDayDu}><span className="block truncate">{tachTenDayDu(route.tenDayDu).ten}</span>{tachTenDayDu(route.tenDayDu).diaChi && <span className="block truncate text-[10px] text-chu-3">{tachTenDayDu(route.tenDayDu).diaChi}</span>}</span>
        <span className="shrink-0 tabular-nums text-chu-3">{route.onlineCount}/{route.cabinetCount}</span>
      </button>

      {open && (
        <div className="ml-2.5 border-l border-vien pl-1.5">
          {route.cabinets.length === 0 && (
            <p className="px-2 py-1.5 text-xs text-chu-3">Tuyến chưa có tủ nào</p>
          )}
          {route.cabinets.map((c) => <CabinetNode key={c.id} cabinet={c} tuyenId={route.id} denKhop={denKhop.get(c.id) ?? []} />)}
        </div>
      )}
    </div>
  );
}

function CabinetNode({ cabinet, tuyenId, denKhop }: { cabinet: TreeCabinet; tuyenId: string; denKhop: DenTim[] }) {
  const online = cabinet.status === 'ONLINE';
  const on = cabinet.outputs.filter((o) => o.isOn).length;

  /* TK-33b: bấm tủ ⇒ trang chính CHỌN tủ ấy (`/?tu=`: bản đồ bay tới + bảng tủ). Đang chọn = trang chính có `tu` này,
     hoặc đang ở chính trang tủ ấy. */
  const loc = useLocation();
  const dangChon = (loc.pathname === '/' && new URLSearchParams(loc.search).get('tu') === cabinet.id) || loc.pathname === `/tu/${cabinet.id}`;
  return (
    <>
    <Link
      to={`/?tuyen=${tuyenId}&tu=${cabinet.id}`}
      aria-current={dangChon ? 'true' : undefined}
      className={[
        'flex min-h-[40px] items-center gap-2 rounded-nut px-2 py-1.5 text-xs transition md:min-h-[30px]',
        dangChon ? 'border-l-[3px] border-nhan bg-nen-3 pl-[5px] text-nhan' : 'text-chu-2 hover:bg-nen-2',
      ].join(' ')}
      data-tu-cay={cabinet.id}
    >
      {/* Chấm trạng thái: xanh đang nối · XÁM mất kết nối (mẫu tô đỏ — luật lee 12/10). */}
      <StatusDot online={online} size="sm" />
      <span className="min-w-0 flex-1 truncate" title={online ? `${on}/${cabinet.outputs.length} ngõ ra bật (tủ tự khai)` : 'Mất kết nối'}>{cabinet.name}</span>
      {/*
        * ⛔ MẤT NỐI ⇒ KHÔNG VẼ SỐ. Huy hiệu "0/3" trên một tủ mất kết nối là một lời khai:
        *   nó nói "ba ngõ đang tắt", trong khi sự thật là **không ai biết**. `—` nói đúng.
        *   Nối lại thì tủ TỰ ĐẨY `CabinetState` ngay (nhánh `lan_dau`), nên khoảng không biết
        *   này không kéo dài — đó là thứ làm cửa "đang nối" đóng kín được.
        */}
      {/* TK-34 lượt 3: mẫu không có huy hiệu trên dòng tủ — số ngõ ra chuyển vào tooltip, giữ luật YC-35 (mất nối ⇒ không nói số) */}
      <span className="sr-only">{online ? `${on}/${cabinet.outputs.length} ngõ ra bật` : 'mất kết nối'}</span>
    </Link>
    {/* TK-48 §4: đèn khớp ô tìm ⇒ dòng đèn dưới tủ, bấm ⇒ trang Vận hành chọn đèn (`?tuyen=&tu=&den=`) */}
    {denKhop.map((d) => (
      <Link key={d.addr} to={`/?tuyen=${tuyenId}&tu=${cabinet.id}&den=${cabinet.id}:${d.addr}`} data-den-cay={`${cabinet.id}:${d.addr}`}
        className="ml-4 flex min-h-[36px] items-center gap-2 rounded-nut px-2 py-1 text-xs text-chu-2 hover:bg-nen-2 md:min-h-[28px]">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-chu-3" aria-hidden="true" /><span className="min-w-0 flex-1 truncate">{tenDen(d)}</span>
      </Link>
    ))}
    </>
  );
}

/** Dùng ở màn hình cần nhắc người dùng rằng vai trò hiện tại chỉ được xem. */
export function ReadOnlyNotice({ need }: { need: Role }) {
  const { user } = useAuth();
  return (
    <div className="flex items-center gap-2 rounded-lg border border-vien bg-nen-2 px-3 py-2 text-xs text-chu-2">
      <ShieldCheck className="h-4 w-4 shrink-0 text-chu-3" />
      Vai trò <b className="mx-1">{user ? ROLE_LABEL[user.role] : ''}</b> chỉ được xem ở khu vực này —
      cần quyền <b className="mx-1">{ROLE_LABEL[need]}</b> trở lên để thao tác.
    </div>
  );
}
